/**
 * Footnote scroll offset and anchor handling.
 *
 * Publishes --navbar-height so anchor targets can clear the fixed header,
 * and makes footnote links land correctly when their target sits inside a
 * collapsed <details> or is opened from a shared URL.
 *
 * The actual offset is applied in CSS via scroll-margin-top; this module only
 * measures the header. See src/scss/components/_footnotes.scss.
 */
import Popover from 'bootstrap/js/dist/popover';

export default (() => {
  /**
   * Fixed elements stacked above the content, top to bottom.
   */
  const HEADER_SELECTORS = [
    '.navbar',
    '.top-bar.gin--navigation-top-bar',
    '.secondary-nav__menu',
  ];

  /**
   * Writes the combined header height to --navbar-height on <html>.
   */
  const setNavbarHeight = () => {
    const total = HEADER_SELECTORS.reduce((sum, selector) => {
      const el = document.querySelector(selector);
      return sum + (el ? el.getBoundingClientRect().height : 0);
    }, 0);

    document.documentElement.style.setProperty('--navbar-height', `${total}px`);
  };

  /**
   * Resolves the element a fragment points at.
   *
   * @param {string} hash
   *   A fragment including the leading '#'.
   *
   * @return {HTMLElement|null}
   *   The target element, or null.
   */
  const resolveTarget = (hash) => {
    if (!hash || hash === '#') {
      return null;
    }

    return document.getElementById(decodeURIComponent(hash.slice(1)));
  };

  /**
   * Expands the <details> wrapping a target, if any.
   *
   * A <tr> inside a collapsed <details> has no layout box, so the browser
   * cannot scroll to it and the click appears to do nothing.
   *
   * @param {HTMLElement|null} target
   *   The element about to be scrolled to.
   */
  const revealTarget = (target) => {
    target?.closest('details')?.setAttribute('open', '');
  };

  /**
   * Re-runs fragment navigation once the header has been measured.
   */
  const scrollToHash = () => {
    const target = resolveTarget(window.location.hash);

    if (!target) {
      return;
    }

    revealTarget(target);
    target.scrollIntoView({ behavior: 'instant' });
  };

  Drupal.behaviors.bisFootnotes = {
    attach(context) {
      once('bis-navbar-height', 'body', context).forEach(() => {
        requestAnimationFrame(() => {
          setNavbarHeight();
          scrollToHash();
        });

        document.fonts?.ready.then(setNavbarHeight);

        window.addEventListener('resize', setNavbarHeight);
        document.addEventListener('shown.bs.collapse', setNavbarHeight);
        document.addEventListener('hidden.bs.collapse', setNavbarHeight);

        HEADER_SELECTORS.forEach((selector) => {
          const el = document.querySelector(selector);

          if (el) {
            new ResizeObserver(setNavbarHeight).observe(el);
          }
        });
      });

      once('bis-footnote-anchor', 'a.footnote__citation', context).forEach(
        (link) => {
          link.addEventListener('click', () => {
            revealTarget(resolveTarget(link.hash));
          });
        },
      );

      // Hover previews. Only where a real pointer exists
      if (window.matchMedia('(hover: hover)').matches) {
        once('bis-footnote-popover', 'a.footnote__citation', context).forEach(
          (link) => {
            const cell = resolveTarget(link.hash)?.querySelector(
              'td:last-child',
            );

            if (!cell) {
              return;
            }

            link.removeAttribute('title');
            link.removeAttribute('data-bs-original-title');

            const popover = new Popover(link, {
              content: cell.innerHTML,
              html: true,
              trigger: 'manual',
              placement: 'top',
              customClass: 'footnote-popover',
            });

            let timer;

            const open = () => {
              clearTimeout(timer);
              popover.show();
            };

            const close = () => {
              clearTimeout(timer);
              timer = setTimeout(() => {
                const tip = document.getElementById(
                  link.getAttribute('aria-describedby'),
                );

                if (!tip?.matches(':hover')) {
                  popover.hide();
                }
              }, 200);
            };

            link.addEventListener('mouseenter', open);
            link.addEventListener('focus', open);
            link.addEventListener('mouseleave', close);
            link.addEventListener('blur', close);

            link.addEventListener('shown.bs.popover', () => {
              const tip = document.getElementById(
                link.getAttribute('aria-describedby'),
              );

              tip?.addEventListener('mouseenter', open);
              tip?.addEventListener('mouseleave', close);
            });
          },
        );
      }
    },
  };
})();
